import {fetchMessarioTemplates} from '@lib/messarioTemplates/api';
import {
  getMessarioTemplatesAccess,
  listMessarioTemplatesAccessUserIds,
  saveMessarioTemplatesAccess
} from '@lib/messarioTemplates/storage';
import {MessarioTemplate, MessarioTemplatesAccess} from '@lib/messarioTemplates/types';

const templatesByUserId = new Map<string, MessarioTemplate[]>();
const accountNameByUserId = new Map<string, string>();
const inflightByUserId = new Map<string, Promise<MessarioTemplate[]>>();
const listeners = new Set<() => void>();

function panelOrigin() {
  return import.meta.env.VITE_MESSARIO_PANEL_ORIGIN as string;
}

export function subscribeMessarioTemplates(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyMessarioTemplatesListeners() {
  for(const listener of listeners) {
    listener();
  }
}

export function getMessarioTemplates(externalUserId: string): MessarioTemplate[] {
  return templatesByUserId.get(externalUserId) ?? [];
}

export function getMessarioAccountName(externalUserId: string): string {
  return accountNameByUserId.get(externalUserId) ?? '';
}

export function rememberMessarioTemplatesAccess(
  externalUserId: string,
  access: MessarioTemplatesAccess
) {
  saveMessarioTemplatesAccess(externalUserId, access);
}

/**
 * One list fetch per external_user_id for the lifetime of this tab page.
 * Chat switches do not trigger a new request.
 */
export function ensureMessarioTemplatesLoaded(
  externalUserId: string,
  fetchValue: typeof fetch = fetch
): Promise<MessarioTemplate[]> {
  if(!externalUserId) {
    return Promise.resolve([]);
  }

  const cached = templatesByUserId.get(externalUserId);
  if(cached) {
    return Promise.resolve(cached);
  }

  const inflight = inflightByUserId.get(externalUserId);
  if(inflight) {
    return inflight;
  }

  const access = getMessarioTemplatesAccess(externalUserId);
  if(!access) {
    return Promise.resolve([]);
  }

  const promise = fetchMessarioTemplates(access.token, panelOrigin(), fetchValue)
    .then((payload) => {
      templatesByUserId.set(externalUserId, payload.templates);
      accountNameByUserId.set(externalUserId, payload.accountName);
      notifyMessarioTemplatesListeners();
      return payload.templates;
    })
    .catch(() => {
      templatesByUserId.set(externalUserId, []);
      accountNameByUserId.set(externalUserId, '');
      notifyMessarioTemplatesListeners();
      return [] as MessarioTemplate[];
    })
    .finally(() => {
      inflightByUserId.delete(externalUserId);
    });

  inflightByUserId.set(externalUserId, promise);
  return promise;
}

export function saveAndLoadMessarioTemplates(
  externalUserId: string,
  access: MessarioTemplatesAccess
): Promise<MessarioTemplate[]> {
  rememberMessarioTemplatesAccess(externalUserId, access);
  // Fresh resolve token: allow one reload if a previous empty/failed cache exists.
  if(templatesByUserId.has(externalUserId) && !inflightByUserId.has(externalUserId)) {
    templatesByUserId.delete(externalUserId);
    accountNameByUserId.delete(externalUserId);
  }
  return ensureMessarioTemplatesLoaded(externalUserId);
}

export function bootstrapMessarioTemplatesFromStorage(
  fetchValue: typeof fetch = fetch
): Promise<void> {
  const userIds = listMessarioTemplatesAccessUserIds();
  if(!userIds.length) {
    return Promise.resolve();
  }

  return Promise.all(userIds.map((userId) => ensureMessarioTemplatesLoaded(userId, fetchValue)))
    .then((): void => undefined);
}

/** @internal — tests only */
export function resetMessarioTemplatesStoreForTests() {
  templatesByUserId.clear();
  accountNameByUserId.clear();
  inflightByUserId.clear();
  listeners.clear();
}
